import React, { useEffect, useState } from "react";
import { ArrowLeft, MessageCircle, Share2, MapPin, Tag, BadgeCheck, Heart, Zap, Star, Minus, Plus, Store, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { fileUrl } from "@/lib/uploads";
import { BlurImage } from "@/lib/blur";
import { getInitials, avatarColor } from "@/lib/avatar";
import LiveRing from "@/components/live/LiveRing";
import ShareSheet from "@/components/shared/ShareSheet";
import SafeTradeBanner from "@/components/shared/SafeTradeBanner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { pushRecentlyViewed } from "@/lib/recent";
import { formatPrice, formatApprox, convert, getBuyerCurrency, useFxRates } from "@/lib/currency";
import { toast } from "sonner";

function Stars({ value, onPick, size = 18 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} disabled={!onPick} onClick={() => onPick && onPick(n)} data-testid={onPick ? `star-${n}` : undefined}>
          <Star size={size} className={n <= value ? "fill-[var(--rc-gold)] text-[var(--rc-gold)]" : "text-[var(--rc-muted)]"} />
        </button>
      ))}
    </div>
  );
}

export default function ProductDetail({ productId, currency, onBack, onMessage, onAddToCart }) {
  const { user } = useAuth();
  const { t } = useI18n();
  useFxRates();
  const [pid, setPid] = useState(productId);
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myReview, setMyReview] = useState("");
  const [sel, setSel] = useState({});          // {optionName: value}
  const [qty, setQty] = useState(1);
  const [shopItems, setShopItems] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => { setPid(productId); }, [productId]);
  useEffect(() => {
    setP(null); setActive(0); setSel({}); setQty(1);
    api.get(`/market/products/${pid}`).then(({ data }) => {
      setP(data);
      pushRecentlyViewed({ id: data.id, title: data.title, image: (data.images || [])[0]?.url, price: data.effective_price ?? data.price, currency: data.currency || currency });
      if (data.shop_id) {
        api.get(`/market/shops/${data.shop_id}/reviews`).then(({ data: r }) => setReviews(r)).catch(() => {});
        api.get(`/market/shops/${data.shop_id}`).then(({ data: s }) => setShopItems((s.products || []).filter((x) => x.id !== data.id).slice(0, 12))).catch(() => {});
      }
    }).catch(() => {});
  }, [pid]);
  if (!p) return <div className="p-8 text-center text-[var(--rc-muted)]">{t("m_loading")}</div>;

  const images = p.images || [];
  const cur = p.currency || currency || "USD";
  const base = Number(p.base_price ?? p.price ?? 0);
  const price = Number(p.effective_price ?? p.price ?? 0);
  const onPromo = !!p.on_promo && price < base;
  const pct = onPromo && base > 0 ? Math.round((1 - price / base) * 100) : 0;
  const buyerCur = getBuyerCurrency();
  const approx = buyerCur !== cur && price > 0 ? convert(price, cur, buyerCur) : null;
  const options = p.variant_options || [];
  const variants = p.variants || [];
  const selKey = options.map((o) => sel[o.name]).filter(Boolean).join(" / ");
  const allSelected = options.length > 0 && options.every((o) => sel[o.name]);
  const selVariant = variants.find((v) => v.key === selKey);
  const variantStock = selVariant ? Number(selVariant.stock || 0) : null;
  const outOfStock = variants.length ? (allSelected ? variantStock <= 0 : !variants.some((v) => v.stock > 0)) : (p.in_stock === false);
  const shownStock = variants.length ? (allSelected ? variantStock : null) : (p.stock_tracked ? p.total_stock : null);
  const isOwner = p.seller?.id === user.id;
  const myExisting = reviews.find((r) => r.author_id === user.id);
  const avg = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : 0;
  const promoEndDays = p.promo_until ? Math.ceil((new Date(p.promo_until) - Date.now()) / 86400000) : 0;

  const doAddToCart = () => {
    if (options.length > 0 && !allSelected) return toast.error(t("m_choose_options_first"));
    if (outOfStock) return;
    onAddToCart && onAddToCart(p, options.length ? selKey : null, qty);
  };

  const share = async () => { setShareOpen(true); };
  const toggleSave = async () => { try { const { data } = await api.post(`/market/products/${p.id}/save`); setP((x) => ({ ...x, saved: data.saved })); toast.success(data.saved ? t("m_saved_toast") : t("m_removed")); } catch {} };
  const submitReview = async () => {
    if (!myRating) return toast.error(t("m_pick_star"));
    try { await api.post(`/market/shops/${p.shop_id}/review`, { rating: myRating, text: myReview }); const { data: r } = await api.get(`/market/shops/${p.shop_id}/reviews`); setReviews(r); setMyReview(""); setMyRating(0); toast.success(t("m_thanks_review")); } catch (e) { toast.error(e?.response?.data?.detail || t("s_failed")); }
  };

  return (
    <div className="pb-28" data-testid="product-detail">
      <button onClick={onBack} data-testid="detail-back" className="flex items-center gap-1.5 text-[var(--rc-muted)] mb-3"><ArrowLeft size={18} /> {t("s_back")}</button>

      {!isOwner && <SafeTradeBanner testid="detail-safe-trade" className="mb-3" />}

      <div className="relative rounded-2xl overflow-hidden bg-[var(--rc-surface)] border border-[var(--rc-border)]">
        {p.boosted && <span className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded-full bg-[var(--rc-gold)] text-black text-[11px] font-bold px-2 py-0.5"><Zap size={12} /> {t("m_boosted")}</span>}
        {onPromo && !p.boosted && <span className="absolute top-2 left-2 z-10 rounded-full bg-red-500 text-white text-[11px] font-bold px-2 py-0.5">-{pct}% {t("m_off")}</span>}
        <button onClick={toggleSave} data-testid="detail-save" className="absolute top-2 right-2 z-10 h-9 w-9 grid place-items-center rounded-full bg-black/50 backdrop-blur">
          <Heart size={18} className={p.saved ? "fill-[var(--rc-gold)] text-[var(--rc-gold)]" : "text-white"} />
        </button>
        <div className="aspect-square bg-[var(--rc-bg)]" data-testid="detail-main-image">
          {images.length ? <BlurImage src={fileUrl(images[active].url)} hash={images[active].blur_hash} className="h-full w-full" imgClassName="h-full w-full object-contain" testid="detail-main-img" />
            : <div className="h-full grid place-items-center text-6xl">🛍️</div>}
        </div>
        {images.length > 1 && (
          <div className="flex gap-2 p-2 overflow-x-auto no-scrollbar">
            {images.map((m, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`detail-thumb-${i}`}
                className={`h-16 w-16 shrink-0 rounded-lg overflow-hidden border-2 ${i === active ? "border-[var(--rc-gold)]" : "border-transparent"}`}>
                <img src={fileUrl(m.url)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Showcase video */}
      {p.video?.url && (
        <video src={fileUrl(p.video.url)} controls playsInline preload="none" data-testid="detail-video"
          className="mt-3 w-full rounded-2xl border border-[var(--rc-border)] bg-black max-h-[60vh]" />
      )}

      <div className="mt-4">
        <h2 className="text-xl font-medium">{p.title}</h2>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-2xl font-heading text-[var(--rc-gold)]" data-testid="detail-price">{base > 0 ? formatPrice(price, cur) : t("m_contact_price")}</span>
          {onPromo && <span className="text-base text-[var(--rc-muted)] line-through">{formatPrice(base, cur)}</span>}
          {onPromo && <span className="text-xs font-bold text-red-500">-{pct}% {t("m_off")}</span>}
        </div>
        {approx != null && <div className="text-xs text-[var(--rc-muted)] mt-0.5" data-testid="detail-approx">{formatApprox(approx, buyerCur)}</div>}
        {onPromo && promoEndDays > 0 && <div className="text-xs text-red-400 mt-0.5">{t("m_ends_in")} {promoEndDays}{t("m_day_short")}</div>}
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-[var(--rc-muted)]">
          {p.location && <span className="flex items-center gap-1"><MapPin size={13} /> {p.location}</span>}
          {p.category && <span className="flex items-center gap-1"><Tag size={13} /> {p.category}</span>}
          {p.condition && <span className="flex items-center gap-1"><BadgeCheck size={13} /> {p.condition}</span>}
        </div>

        {/* Stock status */}
        <div className="mt-2 text-sm font-semibold" data-testid="detail-stock">
          {outOfStock ? <span className="text-red-400">{t("m_out_of_stock")}</span>
            : (typeof shownStock === "number" && shownStock > 0 && shownStock <= 5) ? <span className="text-orange-400">{t("m_only_left").replace("{n}", shownStock)}</span>
              : <span className="text-emerald-400">{t("m_in_stock")}</span>}
        </div>

        {/* Variant selectors */}
        {options.map((o) => (
          <div key={o.name} className="mt-3" data-testid={`detail-option-${o.name}`}>
            <div className="text-sm font-medium mb-1.5">{o.name}</div>
            <div className="flex flex-wrap gap-2">
              {(o.values || []).map((v) => (
                <button key={v} onClick={() => setSel({ ...sel, [o.name]: v })} data-testid={`detail-opt-${o.name}-${v}`}
                  className={`rounded-xl border px-4 py-2 text-sm ${sel[o.name] === v ? "border-[var(--rc-gold)] text-[var(--rc-gold)] bg-[var(--rc-gold)]/10" : "border-[var(--rc-border)] text-[var(--rc-text)]"}`}>{v}</button>
              ))}
            </div>
          </div>
        ))}

        {/* Quantity */}
        {!isOwner && !outOfStock && (
          <div className="mt-3 flex items-center gap-3" data-testid="detail-qty">
            <span className="text-sm font-medium">{t("m_qty")}</span>
            <div className="flex items-center rounded-xl border border-[var(--rc-border)]">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-dec" className="h-9 w-9 grid place-items-center text-[var(--rc-gold)]"><Minus size={16} /></button>
              <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-inc" className="h-9 w-9 grid place-items-center text-[var(--rc-gold)]"><Plus size={16} /></button>
            </div>
          </div>
        )}

        {p.description && <p className="mt-3 text-sm whitespace-pre-wrap [overflow-wrap:anywhere] text-[var(--rc-text)]">{p.description}</p>}

        {/* Desktop actions */}
        {!isOwner && (
          <div className="hidden md:flex gap-2 mt-4">
            {p.sellable ? (
              <>
                <button onClick={doAddToCart} disabled={outOfStock} data-testid="detail-add-cart-desktop"
                  className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-40"><ShoppingCart size={18} /> {t("m_add_to_cart")}</button>
                <button onClick={() => onMessage(p)} data-testid="detail-message-desktop" className="rounded-full border border-[var(--rc-border)] text-[var(--rc-gold)] px-5 py-3 flex items-center gap-2"><MessageCircle size={18} /> {t("m_message_seller")}</button>
              </>
            ) : (
              <button onClick={() => onMessage(p)} data-testid="detail-text-seller-desktop" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-3"><MessageCircle size={18} /> {t("m_text_seller")}</button>
            )}
          </div>
        )}
      </div>

      {/* Seller */}
      <div className="mt-5 flex items-center gap-3 rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-3" data-testid="detail-seller">
        <LiveRing uid={p.seller?.id}>
          <div className="h-11 w-11 rounded-full grid place-items-center text-[var(--rc-gold)] font-semibold border-2 border-[var(--rc-gold)] overflow-hidden" style={{ backgroundColor: p.shop_avatar ? "var(--rc-bg)" : avatarColor(p.shop_name || p.seller?.name || "S") }}>
            {p.shop_avatar ? <img src={fileUrl(p.shop_avatar)} alt="" className="h-full w-full object-cover" /> : getInitials(p.shop_name || p.seller?.name || "S")}
          </div>
        </LiveRing>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[var(--rc-muted)]">{t("m_seller")}</div>
          <div className="font-medium truncate flex items-center gap-1">{p.shop_name || p.seller?.name}{p.shop_verified && <BadgeCheck size={15} className="text-[var(--rc-gold)] shrink-0" data-testid="detail-verified" />}</div>
          {reviews.length > 0 && <div className="flex items-center gap-1 text-xs text-[var(--rc-gold)]"><Star size={12} className="fill-[var(--rc-gold)]" /> {avg} ({reviews.length})</div>}
        </div>
      </div>

      {/* More from this shop */}
      {shopItems.length > 0 && (
        <div className="mt-5" data-testid="more-from-shop">
          <div className="flex items-center gap-1.5 mb-2 font-medium"><Store size={16} className="text-[var(--rc-gold)]" /> {t("m_more_from_shop")}</div>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {shopItems.map((it) => (
              <button key={it.id} onClick={() => setPid(it.id)} data-testid={`more-item-${it.id}`}
                className="w-32 shrink-0 text-left">
                <div className="aspect-square rounded-xl overflow-hidden bg-[var(--rc-bg)] border border-[var(--rc-border)]">
                  {(it.images || [])[0] ? <img src={fileUrl(it.images[0].url)} alt="" className="h-full w-full object-cover" /> : <div className="h-full grid place-items-center text-2xl">🛍️</div>}
                </div>
                <div className="text-xs mt-1 truncate">{it.title}</div>
                <div className="text-xs text-[var(--rc-gold)] font-semibold">{formatPrice(it.effective_price ?? it.price, cur)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      <div className="mt-5" data-testid="shop-reviews">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">{t("m_ratings_reviews")}</h3>
          {reviews.length > 0 && <div className="flex items-center gap-1.5 text-sm"><Stars value={Math.round(avg)} size={15} /><span className="text-[var(--rc-muted)]">{avg}</span></div>}
        </div>
        {!isOwner && !myExisting && (
          <div className="rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-3 mb-3" data-testid="review-form">
            <div className="text-xs text-[var(--rc-muted)] mb-1.5">{t("m_rate_shop")}</div>
            <Stars value={myRating} onPick={setMyRating} />
            <textarea rows={2} value={myReview} onChange={(e) => setMyReview(e.target.value)} placeholder={t("m_write_review")} data-testid="review-text"
              className="w-full mt-2 bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2 text-sm resize-none focus:outline-none" />
            <button onClick={submitReview} data-testid="review-submit" className="mt-2 rounded-full bg-[var(--rc-gold)] text-black text-sm font-semibold px-4 py-2">{t("m_submit_review")}</button>
          </div>
        )}
        {!isOwner && myExisting && (
          <div className="rounded-2xl border border-[var(--rc-gold)]/30 bg-[var(--rc-gold)]/5 p-3 mb-3 flex items-center justify-between" data-testid="review-done">
            <span className="text-sm text-[var(--rc-gold)] flex items-center gap-1.5"><Star size={14} className="fill-[var(--rc-gold)]" /> {t("m_you_rated")}</span>
            <Stars value={myExisting.rating} size={14} />
          </div>
        )}
        {reviews.length ? <div className="space-y-2">{reviews.map((r) => (
          <div key={r.id} className="rounded-xl border border-[var(--rc-border)] bg-[var(--rc-surface)] p-3" data-testid={`review-${r.id}`}>
            <div className="flex items-center gap-2"><span className="font-medium text-sm">{r.author_name}</span><Stars value={r.rating} size={12} /></div>
            {r.text && <div className="text-sm text-[var(--rc-muted)] mt-1 [overflow-wrap:anywhere]">{r.text}</div>}
          </div>
        ))}</div> : <div className="text-sm text-[var(--rc-muted)]">{t("m_no_reviews")}</div>}
      </div>

      {/* sticky actions (mobile) */}
      <div className="fixed bottom-16 left-0 right-0 md:hidden px-4 pb-3">
        <div className="max-w-2xl mx-auto flex gap-2">
          {isOwner ? (
            <button onClick={() => onMessage(p)} data-testid="detail-message" className="flex-1 flex items-center justify-center gap-2 rounded-full border border-[var(--rc-border)] bg-[var(--rc-surface)] text-[var(--rc-gold)] py-3"><MessageCircle size={18} /></button>
          ) : p.sellable ? (
            <>
              <button onClick={doAddToCart} disabled={outOfStock} data-testid="detail-add-cart" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-40"><ShoppingCart size={18} /> {t("m_add_to_cart")}</button>
              <button onClick={() => onMessage(p)} data-testid="detail-message" className="flex items-center justify-center gap-2 rounded-full border border-[var(--rc-border)] bg-[var(--rc-surface)] text-[var(--rc-gold)] py-3 px-4"><MessageCircle size={18} /></button>
            </>
          ) : (
            <button onClick={() => onMessage(p)} data-testid="detail-text-seller" className="flex-1 flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-3"><MessageCircle size={18} /> {t("m_text_seller")}</button>
          )}
          <button onClick={share} data-testid="detail-share" className="h-12 w-12 grid place-items-center rounded-full border border-[var(--rc-border)] bg-[var(--rc-surface)] text-[var(--rc-gold)]"><Share2 size={18} /></button>
        </div>
      </div>
      {shareOpen && p && <ShareSheet kind="product" item={p} onClose={() => setShareOpen(false)} />}
    </div>
  );
}
